import _ from "lodash"
import { addUnderScores, ensureStringFormat } from "../../utils"
import { ProcessConfigSchema, ProcessType, ProcessConfigType, Division, JobType } from "../../types"
import {
  JobSubType,
  DocName,
  Offence,
  SingleOffenceConfig,
  ExtractionConfig,
  ExtractionConfigItem,
  ProcessConfigCategoryEnums,
} from "../../types/processConfigTypes"

export class ProcessConfig {
  constructor(private processConfig: ProcessConfigSchema) {}

  getProcessConfig() {
    return this.processConfig
  }

  getAllProcessNames(): ProcessType[] {
    return Object.keys(this.processConfig) as ProcessType[]
  }

  getDivisionNames(process: ProcessType, processConfigType: ProcessConfigType): Division[] {
    //@ts-ignore TODO: Find a way to get rid of this ts-ignore
    return Object.keys(this.processConfig[process][processConfigType]) as Division[]
  }

  getJobTypes(process: ProcessType, processConfigType: ProcessConfigType, divisionName: Division): JobType[] {
    //@ts-ignore TODO: Find a way to get rid of this ts-ignore
    return Object.keys(this.processConfig[process][processConfigType][divisionName]) as JobType[]
  }

  getJobSubTypes(
    process: ProcessType,
    processConfigType: ProcessConfigType,
    divisionName: Division,
    jobType: JobType,
  ): JobSubType[] {
    // @ts-ignore
    const targetConfig = this.getProcessConfig()[process]
    const data = _.get(targetConfig, `${processConfigType}.${divisionName}`, {}) as Record<Division, JobType>
    return Object.keys(data[jobType]) as JobSubType[]
  }

  getAllPreRequisiteDocNames(): DocName[] {
    const process = ProcessType.competencies
    const configType = ProcessConfigType.requiredCompetencies
    let allPreRequisiteDocNames: DocName[] = []
    const divisionNames = this.getDivisionNames(process, configType)
    for (const divisionName of divisionNames) {
      const jobTypes = this.getJobTypes(process, configType, divisionName)
      for (const jobType of jobTypes) {
        const jobSubTypes = this.getJobSubTypes(process, configType, divisionName, jobType)
        for (const jobSubType of jobSubTypes) {
          const preRequisiteDocs = _.get(
            this.processConfig,
            `${process}.${configType}.${divisionName}.${jobType}.${jobSubType}.requiredDocNames`,
            [],
          ) as DocName[]
          allPreRequisiteDocNames.push(...preRequisiteDocs)
        }
      }
    }
    return Array.from(new Set(allPreRequisiteDocNames))
  }

  getPoorPerformanceOffenceName() {
    return this.getProcessConfig().discipline.poorPerformanceOffenceName || ""
  }

  getOffenceConfigForOffence(offence: Offence): SingleOffenceConfig | undefined {
    const processConfig = this.getProcessConfig()
    if (!processConfig.discipline.offenceConfig.hasOwnProperty(offence)) {
      return
    }
    return processConfig.discipline.offenceConfig[offence]
  }

  getConfiguredProcessInfo = (
    configType: ProcessConfigCategoryEnums,
    process: ProcessType,
    segmentInfo: any,
    fallBackValue: any = {},
  ) => {
    const processConfig = this.getProcessConfig()
    const processConfigTypeMap: Record<ProcessConfigCategoryEnums, string> = {
      dataFlowNames: "dataCaptureConfig",
      docNames: "docCaptureConfig",
      actionFlow: "offenceConfig",
      actionExpiry: "offenceConfig",
      requiredCompetencies: "competencyConfig",
    }
    const {
      division,
      jobType,
      jobSubType,
      reasonForTermination,
      subReasonForTermination,
      eventType,
      action,
      offence,
      competencyName,
    } = segmentInfo

    let extractionConfig: ExtractionConfig = [
      { targetKey: addUnderScores(ensureStringFormat(division)), fallbackKey: "allDivisions" },
      { targetKey: addUnderScores(ensureStringFormat(jobType)), fallbackKey: "allJobTypes" },
      { targetKey: addUnderScores(ensureStringFormat(jobSubType)), fallbackKey: "allJobSubTypes" },
    ]
    if (process === ProcessType.competencies && configType !== ProcessConfigCategoryEnums.requiredCompetencies) {
      extractionConfig = [
        ...extractionConfig,
        { targetKey: addUnderScores(competencyName), fallbackKey: "allCompetencies" },
      ]
    }
    if (process === ProcessType.terminate) {
      extractionConfig = [
        ...extractionConfig,
        { targetKey: addUnderScores(reasonForTermination), fallbackKey: "allTerminationReasons" },
        { targetKey: addUnderScores(subReasonForTermination), fallbackKey: "allTerminationSubReasons" },
      ]
    }
    if (process === ProcessType.discipline) {
      if (processConfigTypeMap[configType] === "offenceConfig") {
        extractionConfig = [{ targetKey: offence, fallbackKey: "allOffences" }, ...extractionConfig]
      } else if (configType) {
        extractionConfig = [
          ...extractionConfig,
          { targetKey: offence, fallbackKey: "allOffences" },
          { targetKey: action, fallbackKey: "allActions" },
        ]
      }
    }
    if (process === ProcessType.eventConfig) {
      extractionConfig = [{ targetKey: eventType, fallbackKey: "allEventTypes" }]
    }
    extractionConfig.push({ targetKey: configType } as ExtractionConfigItem)
    return this.extractTargetConfig(
      (processConfig as Record<string, any>)[process][processConfigTypeMap[configType]],
      extractionConfig,
      fallBackValue,
    )
  }

  private extractTargetConfig = (
    sourceData: Record<string, any>,
    extractionConfig: ExtractionConfig,
    fallBackValue: any,
  ) => {
    let searchString = ""
    let currentDataLevel = _.cloneDeep(sourceData)
    extractionConfig.forEach((configItem, index) => {
      const { targetKey, fallbackKey } = configItem
      const keyToAdd = targetKey && targetKey in currentDataLevel ? targetKey : fallbackKey
      if (searchString) {
        searchString = `${searchString}${keyToAdd ? `["${keyToAdd}"]` : ""}`
      } else {
        searchString = `${keyToAdd ? `["${keyToAdd}"]` : ""}`
      }
      const defaultValue = index === extractionConfig.length - 1 ? fallBackValue : {}
      currentDataLevel = _.get(sourceData, searchString, defaultValue)
    })
    return currentDataLevel
  }
}
